import { CopyOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Drawer, Dropdown, Menu, Modal } from "antd";
import { Fragment, useCallback, useEffect, useState } from "react";
import { GET_ALL_NEW_TNA_TASK_DATA, GET_TNA_TEMPLATE_RESPONSIBLE_USERS_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import { isArrayAndHasValue } from "utils/functions";
import ItemEdit from "../ItemEdit";

const mergeUsersWithResponsiblePersons = (users, persons) => {
  const merged = [...(users || [])];

  (persons || []).forEach((person) => {
    if (person?.id && !merged.some((user) => user?.id === person.id)) {
      merged.push({ id: person.id, name: person.name });
    }
  });

  return merged;
};

const selectableCellStyle = { userSelect: "text", WebkitUserSelect: "text" };

const ItemDetails = ({ view, itemDetailsList, setItemDetailsList }) => {
  const [TNATaskList, setTNATaskList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { confirm } = Modal;

  const getTNATaskList = async (title) => {
    const response = await getData(GET_ALL_NEW_TNA_TASK_DATA, false, {
      per_page: 1000,
      ...(title ? { title } : {}),
    });

    if (response?.data?.code === 200) {
      setTNATaskList(response?.data?.data?.data || []);
    }
  };

  const getResponsiblePersonsFromItems = useCallback(() => {
    return (itemDetailsList || [])
      .filter((item) => item?.responsible_person_id && item?.responsible_person_name)
      .map((item) => ({
        id: item.responsible_person_id,
        name: item.responsible_person_name,
      }));
  }, [itemDetailsList]);

  const getUserList = useCallback(
    async (name) => {
      const response = await getData(GET_TNA_TEMPLATE_RESPONSIBLE_USERS_ENDPOINT, false, {
        per_page: 100,
        ...(name ? { name } : {}),
      });

      if (response?.data?.code === 200) {
        const users = response?.data?.data?.data || [];
        const personsToMerge = getResponsiblePersonsFromItems();

        if (selectedItem?.responsible_person_id && selectedItem?.responsible_person_name) {
          personsToMerge.push({
            id: selectedItem.responsible_person_id,
            name: selectedItem.responsible_person_name,
          });
        }

        setUserList(mergeUsersWithResponsiblePersons(users, personsToMerge));
      }
    },
    [selectedItem, getResponsiblePersonsFromItems],
  );

  useEffect(() => {
    getTNATaskList();
    getUserList();
  }, [getUserList]);

  useEffect(() => {
    const persons = getResponsiblePersonsFromItems();
    if (!persons.length) {
      return;
    }

    setUserList((prev) => mergeUsersWithResponsiblePersons(prev, persons));
  }, [getResponsiblePersonsFromItems]);

  const onAddOpen = () => {
    setSelectedItem(null);
    setIsDrawerOpen(true);
  };

  const onEditOpen = (index) => {
    if (view) {
      return;
    }

    const selected = itemDetailsList[index] || null;
    setSelectedItem({ ...selected, index });

    if (selected?.responsible_person_id && selected?.responsible_person_name) {
      setUserList((prev) =>
        mergeUsersWithResponsiblePersons(prev, [
          {
            id: selected.responsible_person_id,
            name: selected.responsible_person_name,
          },
        ]),
      );
    }

    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setSelectedItem(null);
    setIsDrawerOpen(false);
  };

  const onAddNewItem = (values) => {
    setItemDetailsList((prev) => [...prev, { ...values, is_add: true }]);
    closeDrawer();
    HandleNotification("success", "bottomRight", "Task added", null);
  };

  const onUpdateItem = (index, values) => {
    const copied = structuredClone(itemDetailsList);
    copied[index] = {
      ...copied[index],
      ...values,
    };

    setItemDetailsList(copied);
    closeDrawer();
    HandleNotification("success", "bottomRight", "Task updated", null);
  };

  const onCopyItem = (row) => {
    setItemDetailsList((prev) => [...prev, { ...row, id: null, is_add: true }]);
    HandleNotification("success", "bottomRight", "Task copied", null);
  };

  const removeSelectedTask = (index) => {
    confirm({
      title: "Are you sure delete this task?",
      onOk() {
        const copied = structuredClone(itemDetailsList);
        copied.splice(index, 1);
        setItemDetailsList(copied);
        HandleNotification("success", "bottomRight", "Task deleted", null);
      },
    });
  };

  const menu = (row, index) => {
    return (
      <Menu
        items={[
          {
            key: "edit",
            label: (
              <span onClick={() => onEditOpen(index)}>
                <EditOutlined /> <span className="ml-2">Edit</span>
              </span>
            ),
          },
          {
            key: "copy",
            label: (
              <span onClick={() => onCopyItem(row)}>
                <CopyOutlined /> <span className="ml-2">Copy</span>
              </span>
            ),
          },
          {
            key: "delete",
            label: (
              <span onClick={() => removeSelectedTask(index)}>
                <DeleteOutlined style={{ color: "red" }} />
                <span className="ml-2" style={{ color: "red" }}>
                  Delete
                </span>
              </span>
            ),
          },
        ]}
      />
    );
  };

  return (
    <Card className="custome-table scrollable-table-container" title={<span style={{ fontSize: 14 }}>Task List</span>}>
      <div style={{ display: "flex", marginBottom: 30 }}>
        <div className="custome-table">
          <table>
            <thead>
              <tr>
                <th className="t-header bc-java">SL</th>
                <th className="t-header bc-java">Task</th>
                <th className="t-header bc-java">Process</th>
                <th className="t-header bc-java">Relation With</th>
                <th className="t-header bc-java">Days</th>
                <th className="t-header bc-java">Responsible Person</th>
                {!view && <th className="t-header bc-java">Action</th>}
              </tr>
            </thead>
            <tbody>
              {isArrayAndHasValue(itemDetailsList) &&
                itemDetailsList.map((row, index) => (
                  <Fragment key={`${row?.task_id}-${index}`}>
                    <tr
                      onMouseDown={(event) => {
                        if (event.detail > 1) {
                          event.preventDefault();
                        }
                      }}
                      onDoubleClick={() => onEditOpen(index)}
                    >
                      <td className="t-body" style={selectableCellStyle}>{index + 1}</td>
                      <td className="t-body" style={selectableCellStyle}>{row?.task_name}</td>
                      <td className="t-body" style={selectableCellStyle}>{row?.process_name}</td>
                      <td className="t-body" style={selectableCellStyle}>{row?.relation_with}</td>
                      <td className="t-body" style={selectableCellStyle}>{row?.days}</td>
                      <td className="t-body" style={selectableCellStyle}>{row?.responsible_person_name}</td>
                      {!view && (
                        <td className="t-body">
                          <Dropdown overlay={menu(row, index)}>
                            <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {!view && (
        <Button
          style={{ width: 200, position: "fixed", bottom: "3vh" }}
          type="primary"
          className="mt-2"
          icon={<PlusOutlined />}
          size="small"
          onClick={onAddOpen}
        >
          Add Task
        </Button>
      )}

      <Drawer
        title={selectedItem ? `Task #${selectedItem?.index + 1} Edit` : "Task Add"}
        placement="right"
        onClose={closeDrawer}
        open={isDrawerOpen}
        width={550}
        maskStyle={{
          backdropFilter: "blur(0px)",
          backgroundColor: "rgba(0,0,0,0)",
        }}
      >
        <ItemEdit
          selectedItem={selectedItem}
          getTNATaskList={getTNATaskList}
          TNATaskList={TNATaskList}
          closeDrawer={closeDrawer}
          onUpdateItem={onUpdateItem}
          onAddNewItem={onAddNewItem}
          isDrawerOpen={isDrawerOpen}
          userList={userList}
          getUserList={getUserList}
        />
      </Drawer>
    </Card>
  );
};

export default ItemDetails;
